import { ArrowRight, Check, Eye, WalletCards } from 'lucide-react';
import { useState } from 'react';

import { useI18n } from '../../i18n/useI18n';
import { AddMovementDialog } from '../dashboard/AddMovementDialog';
import type { DashboardSnapshot } from '../dashboard/dashboard.types';
import { WalletEditorDialog } from '../wallets/WalletEditorDialog';

const WALLET_CONFIRMED_KEY = 'financego-onboarding-wallet-confirmed-v1';

type SetupStep = 'expense' | 'income' | 'wallet';

interface FirstProjectionGuideProps {
  readonly onTransactionCreated: (transactionId: string) => void;
  readonly snapshot: DashboardSnapshot;
}

export function FirstProjectionGuide({
  onTransactionCreated,
  snapshot,
}: FirstProjectionGuideProps) {
  const { t } = useI18n();
  const hasIncome = snapshot.rawTransactions.some(
    (transaction) => transaction.active && transaction.type === 'income',
  );
  const hasExpense = snapshot.rawTransactions.some(
    (transaction) => transaction.active && transaction.type === 'expense',
  );
  const [walletConfirmed, setWalletConfirmed] = useState(
    () =>
      snapshot.rawTransactions.length > 0 ||
      sessionStorage.getItem(WALLET_CONFIRMED_KEY) === 'true',
  );
  const [collapsed, setCollapsed] = useState(false);
  const [exampleVisible, setExampleVisible] = useState(false);
  const [dialog, setDialog] = useState<SetupStep | null>(null);

  if (hasIncome && hasExpense) return null;

  const step: SetupStep = !walletConfirmed ? 'wallet' : !hasIncome ? 'income' : 'expense';
  const stepNumber = step === 'wallet' ? 1 : step === 'income' ? 2 : 3;
  const stepContent = {
    expense: {
      action: t('onboarding.expenseAction'),
      description: t('onboarding.expenseDescription'),
      question: t('onboarding.expenseQuestion'),
    },
    income: {
      action: t('onboarding.incomeAction'),
      description: t('onboarding.incomeDescription'),
      question: t('onboarding.incomeQuestion'),
    },
    wallet: {
      action: t('onboarding.walletAction'),
      description: t('onboarding.walletDescription'),
      question: t('onboarding.walletQuestion'),
    },
  }[step];

  const confirmWallet = () => {
    sessionStorage.setItem(WALLET_CONFIRMED_KEY, 'true');
    setWalletConfirmed(true);
  };

  if (collapsed) {
    return (
      <button className="setup-resume" type="button" onClick={() => setCollapsed(false)}>
        <WalletCards aria-hidden="true" />
        <span>
          <strong>{t('onboarding.resume')}</strong>
          <small>{t('onboarding.step', { current: stepNumber, total: 3 })}</small>
        </span>
        <ArrowRight aria-hidden="true" />
      </button>
    );
  }

  return (
    <>
      <section className="setup-guide" aria-labelledby="setup-guide-title">
        <header>
          <div>
            <p className="section-kicker">
              {t('onboarding.step', { current: stepNumber, total: 3 })}
            </p>
            <h2 id="setup-guide-title">{stepContent.question}</h2>
          </div>
          <ol className="setup-progress" aria-label={t('onboarding.progress')}>
            {[1, 2, 3].map((item) => (
              <li className={item <= stepNumber ? 'is-current' : undefined} key={item}>
                {item < stepNumber ? <Check aria-hidden="true" /> : item}
              </li>
            ))}
          </ol>
        </header>
        <p>{stepContent.description}</p>
        <div className="setup-actions">
          <button className="button button-primary" type="button" onClick={() => setDialog(step)}>
            {stepContent.action} <ArrowRight aria-hidden="true" />
          </button>
          <button
            className="button button-ghost button-small"
            type="button"
            onClick={() => setExampleVisible((visible) => !visible)}
          >
            <Eye aria-hidden="true" /> {t('onboarding.exampleAction')}
          </button>
          <button className="setup-later" type="button" onClick={() => setCollapsed(true)}>
            {t('onboarding.later')}
          </button>
        </div>
        {exampleVisible && (
          <div className="setup-example" role="note">
            <span>{t('onboarding.exampleLabel')}</span>
            <strong>$3,180</strong>
            <small>{t('onboarding.exampleDescription')}</small>
          </div>
        )}
      </section>

      {dialog === 'wallet' && (
        <WalletEditorDialog
          linkedMovements={0}
          wallets={snapshot.wallets}
          {...(snapshot.wallets[0] ? { wallet: snapshot.wallets[0] } : {})}
          onClose={() => setDialog(null)}
          onSaved={confirmWallet}
        />
      )}
      {(dialog === 'income' || dialog === 'expense') && (
        <AddMovementDialog
          categories={snapshot.categories}
          initialType={dialog}
          onClose={() => setDialog(null)}
          onCreated={onTransactionCreated}
          today={snapshot.today}
          wallets={snapshot.wallets}
        />
      )}
    </>
  );
}
